import {useEffect, useEffectEvent, useRef} from 'react';

import {clearRegionHoverHighlights, setRegionHoverHighlights} from './annotationPaintCoordinator';
import type {TextProjection} from './annotationTextProjection';
import type {Annotation} from './annotationTypes';
import {
  activeSelectionRange,
  findAnnotationAtPoint,
  isHighlightApiSupported,
  resolveAnnotationRanges
} from './annotationUtils';

interface MarkHoverPaintOptions {
  regionId: string;
  container: HTMLElement | null;
  annotations: Annotation[];
  getProjection: () => TextProjection | null;
  /** A click on a mark opens its editor: the mode is on and still usable. */
  canEdit: boolean;
}

interface Pointer {
  x: number;
  y: number;
  isButtonDown: boolean;
}

interface HoveredMark {
  annotation: Annotation;
  projection: TextProjection;
  isPressed: boolean;
}

/**
 * Paints the mark under the pointer, every piece of it, in its hover shade while a click
 * on it would open the editor, and in its pressed shade while the button is held. It
 * hit-tests as the click does, at most once a frame, and again when a scroll moves the
 * marks under a resting pointer. It paints nothing in a read-only view, a submitted
 * attempt, or with the mode off.
 */
export const useMarkHoverPaint = ({
  regionId,
  container,
  annotations,
  getProjection,
  canEdit
}: MarkHoverPaintOptions): void => {
  const pointerRef = useRef<Pointer | null>(null);
  const hoveredRef = useRef<HoveredMark | null>(null);

  const markUnderPointer = (marks: Annotation[]): HoveredMark | null => {
    const pointer = pointerRef.current;
    // A drag is a new selection, not a click on the mark it started from.
    if (!container || !pointer || (pointer.isButtonDown && activeSelectionRange())) {
      return null;
    }
    const projection = marks.length > 0 ? getProjection() : null;
    const id =
      projection && findAnnotationAtPoint(container, projection, pointer.x, pointer.y, marks);
    const annotation = id ? marks.find((candidate) => candidate.id === id) : undefined;
    return annotation && projection
      ? {annotation, projection, isPressed: pointer.isButtonDown}
      : null;
  };

  const repaintHover = useEffectEvent((marks: Annotation[] = annotations) => {
    const hovered = markUnderPointer(marks);
    const previous = hoveredRef.current;
    // A fresh projection means the region's DOM changed under the painted ranges.
    if (
      hovered?.annotation === previous?.annotation &&
      hovered?.projection === previous?.projection &&
      hovered?.isPressed === previous?.isPressed
    ) {
      return;
    }
    hoveredRef.current = hovered;
    if (hovered) {
      setRegionHoverHighlights(
        regionId,
        resolveAnnotationRanges(hovered.projection, [hovered.annotation]),
        hovered.isPressed
      );
    } else {
      clearRegionHoverHighlights(regionId);
    }
  });

  useEffect(() => {
    if (!container || !canEdit || !isHighlightApiSupported()) {
      return undefined;
    }
    let frame = 0;
    const scheduleRepaint = () => {
      frame ||= requestAnimationFrame(() => {
        frame = 0;
        repaintHover();
      });
    };
    const trackPointer = (event: PointerEvent) => {
      if (event.pointerType === 'touch') {
        return;
      }
      pointerRef.current = {x: event.clientX, y: event.clientY, isButtonDown: event.buttons !== 0};
      scheduleRepaint();
    };
    const trackPress = (event: PointerEvent) => {
      if (event.target instanceof Node && container.contains(event.target)) {
        trackPointer(event);
      }
    };
    const retestAfterScroll = () => {
      if (pointerRef.current) {
        scheduleRepaint();
      }
    };
    const forgetPointer = () => {
      pointerRef.current = null;
      repaintHover();
    };
    // On the document, capturing: the freeze shield stops presses before they reach the
    // region, and scroll never bubbles.
    document.addEventListener('pointerdown', trackPress, true);
    document.addEventListener('scroll', retestAfterScroll, {capture: true, passive: true});
    container.addEventListener('pointermove', trackPointer);
    container.addEventListener('pointerup', trackPointer);
    container.addEventListener('pointerleave', forgetPointer);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('pointerdown', trackPress, true);
      document.removeEventListener('scroll', retestAfterScroll, true);
      container.removeEventListener('pointermove', trackPointer);
      container.removeEventListener('pointerup', trackPointer);
      container.removeEventListener('pointerleave', forgetPointer);
      pointerRef.current = null;
      hoveredRef.current = null;
      clearRegionHoverHighlights(regionId);
    };
  }, [container, canEdit, regionId]);

  // A mark under a resting pointer can be recolored, reshaped or removed.
  useEffect(() => {
    repaintHover(annotations);
  }, [annotations]);
};
